import { v4 as uuidv4 } from 'uuid';

const SESSION_USER_ID_PREFIX = 'dante_session_user_id';

/**
 * Gets or creates a persistent user ID for the current browser session and specific kb_id.
 * This ID will be reused across all requests within the same browser session for the same kb_id
 * until the session ends (browser tab/window is closed).
 * 
 * @param {string} kbId - The knowledge base ID to create a unique userId for
 * @returns {string} A unique user ID for the current session and kb_id
 */
export const getSessionUserId = (kbId) => {
  if (!kbId) {
    throw new Error('kbId is required to generate a session userId');
  }
  
  const sessionKey = `${SESSION_USER_ID_PREFIX}_${kbId}`;
  
  // Try to get existing userId from sessionStorage
  let userId = sessionStorage.getItem(sessionKey);
  
  // If no userId exists, generate a new one and store it
  if (!userId) {
    userId = uuidv4();
    sessionStorage.setItem(sessionKey, userId);
  }
  
  return userId;
};

/**
 * Clears the session user ID from sessionStorage for a specific kb_id.
 * This can be useful for testing or if you need to reset the user session.
 * 
 * @param {string} kbId - The knowledge base ID to clear the userId for
 */
export const clearSessionUserId = (kbId) => {
  if (!kbId) {
    throw new Error('kbId is required to clear a session userId');
  }
  
  const sessionKey = `${SESSION_USER_ID_PREFIX}_${kbId}`;
  sessionStorage.removeItem(sessionKey);
};

/**
 * Clears all session user IDs from sessionStorage.
 * This will clear userIds for all kb_ids.
 */
export const clearAllSessionUserIds = () => {
  const keys = Object.keys(sessionStorage);
  keys.forEach(key => {
    if (key.startsWith(SESSION_USER_ID_PREFIX)) {
      sessionStorage.removeItem(key);
    }
  });
};

/**
 * Checks if a session user ID exists in sessionStorage for a specific kb_id.
 * 
 * @param {string} kbId - The knowledge base ID to check for
 * @returns {boolean} True if a session user ID exists for the kb_id, false otherwise
 */
export const hasSessionUserId = (kbId) => {
  if (!kbId) {
    throw new Error('kbId is required to check for a session userId');
  }
  
  const sessionKey = `${SESSION_USER_ID_PREFIX}_${kbId}`;
  return sessionStorage.getItem(sessionKey) !== null;
};
